import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./WebDevelopment.css";

const CONTACT = "/contact?service=Website%20Development";

const builds = [
  ["01", "Custom WordPress Websites", "A site structured around your content, rather than a stock theme."],
  ["02", "Business Websites", "A clear public home for services, people, and how to get in touch."],
  ["03", "Marketing Websites", "Pages that explain an offer and give the visitor a next step."],
  ["04", "Service Business Websites", "Service lists, locations, and inquiry paths for firms that sell time."],
  ["05", "Real Estate Websites", "Listings, property details, and a direct way for buyers to inquire."],
  ["06", "Healthcare Websites", "Practice information, services, and an appointment path patients can follow."],
  ["07", "Legal Websites", "Practice areas, attorney profiles, and a straightforward contact route."],
  ["08", "Lead Generation Websites", "Focused pages built to turn a visit into a qualified inquiry."],
  ["09", "Booking Websites", "Scheduling placed in the site when an appointment is the action."],
  ["10", "Content-Driven Websites", "A publishing structure for articles, resources, and ongoing updates."]
];

const wordpressGroups = [
  ["window", "Custom WordPress", "Development shaped around the business."],
  ["layers", "Themes", "Custom themes, and Elementor when it fits."],
  ["lines", "Content", "Gutenberg, custom post types, and custom fields."],
  ["code", "Functionality", "Custom PHP, plugins, and business features."],
  ["nodes", "Business tools", "Forms, booking, and CRM."],
  ["link", "Integrations", "APIs and third-party systems."]
];

const wpNodes = [
  ["themes", "Custom Themes", "secondary", "18%", "16%", "240ms", "420ms", "Custom layouts and frontend experiences built around your brand."],
  ["content", "Content Types", "secondary", "78%", "18%", "300ms", "490ms", "Post types and fields structured around the content."],
  ["php", "Custom PHP", "secondary", "87%", "48%", "360ms", "560ms", "PHP, plugins, and features where a theme is not enough."],
  ["apis", "APIs", "secondary", "70%", "76%", "420ms", "630ms", "Connect WordPress with the tools your business already uses."],
  ["forms", "Forms", "tertiary", "14%", "46%", "320ms", "520ms", "Inquiries captured on the site and ready to route."],
  ["booking", "Booking", "tertiary", "22%", "78%", "480ms", "700ms", "Appointment and scheduling workflows connected to your website."],
  ["crm", "CRM", "tertiary", "46%", "84%", "540ms", "760ms", "Customer records passed through when the CRM allows it."],
  ["commerce", "E-commerce", "tertiary", "48%", "12%", "280ms", "600ms", "Product and checkout flows when the site needs to sell."]
];

const wpCaption = "WordPress at the center. Custom systems connected around it.";

const performancePoints = [
  ["frame", "Responsive design", "Layouts that hold together across phones, tablets, and desktops."],
  ["phone", "Mobile-first", "Interfaces designed for smaller screens first."],
  ["speed", "Performance", "Fewer blockers between a visitor and a usable page."],
  ["image", "Image optimization", "Images served for the layout and the device."],
  ["code", "Clean frontend", "Markup and styles another developer can work with."],
  ["seo", "SEO structure", "Headings and page structure search engines can read."],
  ["secure", "Security", "Updates, forms, access, and dependencies treated as part of the build."],
  ["chart", "Analytics", "A connection so the business can see how visitors use the site."]
];

const process = [
  ["Discover", "Understand the business, goals, audience, and requirements."],
  ["Plan", "Define structure, functionality, content, and technical requirements."],
  ["Design", "Create the visual direction and user experience."],
  ["Build", "Develop the frontend, WordPress functionality, integrations, and custom features."],
  ["Test", "Check responsiveness, functionality, forms, integrations, and performance."],
  ["Launch", "Deploy the website and verify the production environment."],
  ["Support", "Continue maintaining and improving the website."]
];

const principles = [
  ["Business first", "Understand the actual business problem before deciding what to build."],
  ["Custom where it matters", "Use the right tools, and add custom functionality where the business needs it."],
  ["Built for real users", "Design around customers, employees, and the people actually using the website."],
  ["Ready to grow", "Create a foundation that can evolve as the business changes."]
];

const faqs = [
  ["What types of websites do you build?", "Business, marketing, service, real estate, healthcare, legal, lead-generation, booking, and content sites, including custom WordPress builds. The page list follows the business."],
  ["Can you work with an existing WordPress website?", "Yes, when the current theme, plugins, and hosting can support the change. We review what should stay, what should be rebuilt, and what is safer to replace."],
  ["Can you build custom WordPress functionality?", "Yes. That can include custom themes, post types, fields, plugins, PHP, forms, booking, and API connections when a standard page is not enough."],
  ["Can you integrate third-party systems?", "Yes, where the other system offers an API, webhook, or a supported connection. Typical cases are CRM, email, payments, booking, and analytics."],
  ["Can you build a website from scratch?", "Yes. A new site starts from the business, the audience, and the actions the site needs to support, then moves through structure, design, and build."],
  ["Can you redesign an existing website?", "Yes. A redesign can keep the content that still works and replace the structure, design, or functionality that does not."],
  ["Do you provide ongoing maintenance?", "Yes. Maintenance can cover updates, fixes, and later changes. That scope is agreed separately from the initial build."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`wd-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="wd-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function BrowserMock() {
  return (
    <div className="wd-browser rounded-xl border border-slate-200 bg-white shadow-[0_30px_70px_-40px_rgba(15,23,42,0.45)]">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <div className="ml-3 h-7 flex-1 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-400 flex items-center px-3 font-mono">
          bizlogic.site
        </div>
      </div>
      <div className="wd-site">
        <div className="wd-ui wd-site-nav" style={{ animationDelay: "820ms" }}>
          <span className="wd-site-brand">
            <i className="wd-site-mark" aria-hidden="true" />
            <strong>BizLogic</strong>
          </span>
          <span className="wd-site-links">
            <span>Home</span>
            <span>Services</span>
            <span className="wd-site-about">About</span>
          </span>
          <span className="wd-site-navbtn">Contact</span>
        </div>
        <div className="wd-ui wd-site-hero" style={{ animationDelay: "960ms" }}>
          <p>Built for your business</p>
          <strong>Digital experiences that move your business forward.</strong>
          <span>Websites designed around your customers, content, and goals.</span>
          <em>Explore Services</em>
        </div>
        <div className="wd-site-cards">
          <article className="wd-ui" style={{ animationDelay: "1120ms" }}>
            <p>Website</p>
            <strong>Built around your business</strong>
          </article>
          <article className="wd-ui" style={{ animationDelay: "1200ms" }}>
            <p>Services</p>
            <ul>
              <li>Web Development</li>
              <li>WordPress</li>
              <li>Custom Experiences</li>
            </ul>
          </article>
          <article className="wd-ui" style={{ animationDelay: "1280ms" }}>
            <p>Performance</p>
            <ul>
              <li>Fast</li>
              <li>Optimized</li>
              <li>Responsive</li>
            </ul>
          </article>
        </div>
      </div>
    </div>
  );
}

function SystemDiagram() {
  const [ref, shown] = useInView(0.35);
  return (
    <div ref={ref} className={`wd-system mx-auto flex w-full max-w-md flex-col items-center ${shown ? "is-live" : ""}`}>
      <div className="wd-node is-core w-full">Website</div>
      <div className="wd-stem" style={{ transitionDelay: "80ms" }} />
      <div className="wd-split">
        <div className="flex flex-col items-center">
          <div className="wd-stem" style={{ transitionDelay: "180ms" }} />
          <div className="wd-node w-full">Leads</div>
          <div className="wd-stem" style={{ transitionDelay: "320ms" }} />
          <div className="wd-node w-full">CRM</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="wd-stem" style={{ transitionDelay: "240ms" }} />
          <div className="wd-node w-full">Bookings</div>
          <div className="wd-stem" style={{ transitionDelay: "380ms" }} />
          <div className="wd-node w-full">Customer data</div>
        </div>
      </div>
      <div className="wd-merge mt-0" />
      <div className="wd-stem" style={{ transitionDelay: "480ms" }} />
      <div className="wd-node is-core w-full">Your business</div>
    </div>
  );
}

function WpGlyph({ type }) {
  const props = { viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.4", "aria-hidden": true };
  if (type === "layers") {
    return <svg {...props}><rect x="3.5" y="3.5" width="9" height="9" /><path d="M5 2.5h8.5V11" /></svg>;
  }
  if (type === "lines") {
    return <svg {...props}><path d="M2.5 4.5h11M2.5 8h8M2.5 11.5h11" /></svg>;
  }
  if (type === "code") {
    return <svg {...props}><path d="M6 3.5 2.5 8 6 12.5M10 3.5 13.5 8 10 12.5" /></svg>;
  }
  if (type === "nodes") {
    return <svg {...props}><rect x="1.5" y="6.2" width="3.4" height="3.4" /><rect x="11.1" y="1.5" width="3.4" height="3.4" /><rect x="11.1" y="11.1" width="3.4" height="3.4" /><path d="M4.9 7.4 11.1 3.6M4.9 8.6 11.1 12.4" /></svg>;
  }
  if (type === "link") {
    return <svg {...props}><rect x="1.5" y="8" width="5" height="5" /><rect x="9.5" y="3" width="5" height="5" /><path d="M6.5 10.5 9.5 7.5" /></svg>;
  }
  return <svg {...props}><rect x="1.5" y="1.5" width="13" height="13" /><path d="M1.5 6h13M6 6v8.5" /></svg>;
}

function PerfMark({ type }) {
  const props = { viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.4", "aria-hidden": true };
  if (type === "phone") return <svg {...props}><rect x="5" y="1.5" width="6" height="13" rx="1" /><path d="M7.2 12.8h1.6" /></svg>;
  if (type === "speed") return <svg {...props}><path d="M2.5 10.5a5.5 5.5 0 1 1 11 0" /><path d="M8 10.5 10.8 6.8" /></svg>;
  if (type === "image") return <svg {...props}><rect x="1.5" y="3" width="13" height="10" /><path d="m3.5 11 3-3 2 2 2-2 2 3" /></svg>;
  if (type === "code") return <svg {...props}><path d="M6 3.5 2.5 8 6 12.5M10 3.5 13.5 8 10 12.5" /></svg>;
  if (type === "seo") return <svg {...props}><path d="M3 4.5h10M3 8h7M3 11.5h10" /></svg>;
  if (type === "secure") return <svg {...props}><rect x="3" y="7" width="10" height="7" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></svg>;
  if (type === "chart") return <svg {...props}><path d="M2.5 13.5h11" /><path d="M4.5 13.5v-4M8 13.5V6M11.5 13.5V3.5" /></svg>;
  return <svg {...props}><rect x="1.5" y="2.5" width="13" height="11" /><path d="M1.5 6h13" /></svg>;
}

function WpArchitecture() {
  const [ref, shown] = useInView(0.28);
  const [active, setActive] = useState(null);
  const current = wpNodes.find((node) => node[0] === active);
  const caption = current ? current[7] : wpCaption;

  return (
    <div ref={ref} className={`wd-wp-visual ${shown ? "is-live" : ""} ${active ? "is-hot" : ""}`}>
      <div className="wd-wp-stage">
        <svg className="wd-wp-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {wpNodes.map(([id, , , x, y, lineDelay]) => (
            <line
              key={id}
              x1="50"
              y1="46"
              x2={parseFloat(x)}
              y2={parseFloat(y)}
              pathLength="1"
              className={active === id ? "is-hot" : ""}
              style={{ "--d": lineDelay }}
            />
          ))}
        </svg>
        <article className="wd-wp-core">
          <header className="wd-wp-core-head">
            <span className="wd-wp-mark"><WpGlyph type="window" /></span>
            <span>
              <strong>WordPress</strong>
              <em>CMS platform</em>
            </span>
            <span className="wd-wp-core-tag"><i />Core</span>
          </header>
          <ul className="wd-wp-ui">
            {["Pages", "Content", "Media", "Users"].map((item, index) => (
              <li key={item} style={{ "--i": index }}>
                <span>{item}</span>
                <b />
              </li>
            ))}
          </ul>
        </article>
        <div className="wd-wp-nodes">
          {wpNodes.map(([id, label, tier, x, y, , nodeDelay, detail]) => (
            <button
              key={id}
              type="button"
              className={`wd-wp-node is-${tier} ${active === id ? "is-hot" : ""}`}
              style={{ "--x": x, "--y": y, "--n": nodeDelay }}
              aria-pressed={active === id}
              onMouseEnter={() => setActive(id)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(id)}
              onBlur={() => setActive(null)}
            >
              <span className="wd-wp-node-label">{label}</span>
              <span className="wd-wp-node-detail">{detail}</span>
            </button>
          ))}
        </div>
      </div>
      <p className="wd-wp-caption" aria-live="polite">{caption}</p>
    </div>
  );
}

function ConnectedFlow() {
  const [ref, shown] = useInView(0.3);
  const steps = ["Website", "Forms", "CRM", "Email", "Booking", "Analytics"];
  return (
    <div ref={ref} className={`wd-flow ${shown ? "is-live" : ""}`}>
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          <div className="wd-node">{step}</div>
          {index < steps.length - 1 ? <div className="wd-stem" style={{ transitionDelay: `${index * 70}ms` }} /> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function PerformancePanel() {
  const [ref, shown] = useInView(0.4);
  const meters = [
    ["Performance", "92%"],
    ["Mobile", "100%"],
    ["Accessibility", "88%"]
  ];
  return (
    <div ref={ref} className="wd-perf-panel">
      <div className="wd-perf-panel-head">
        <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-400 font-heading">Interface example</p>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-100 px-2 py-1">Example</span>
      </div>
      <div className="wd-perf-meters">
        {meters.map(([label, level]) => (
          <div key={label}>
            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold text-slate-900 font-heading">{label}</span>
              <span className="font-mono text-slate-500">{level}</span>
            </div>
            <div className={`wd-meter ${shown ? "is-on" : ""}`}>
              <span style={{ "--wd-level": level }} />
            </div>
          </div>
        ))}
      </div>
      <p className="wd-perf-note">
        These figures are a visual example. They are not measured results for BizLogic or for a client site.
      </p>
    </div>
  );
}

function ProcessViz({ index }) {
  if (index === 0) {
    return (
      <div className="wd-viz wd-viz-net">
        {["Goals", "Audience", "Requirements", "Business"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="wd-viz wd-viz-flow">
        {["Content", "Design", "Functionality", "Integrations"].map((label, step) => (
          <React.Fragment key={label}>
            {step > 0 ? <i aria-hidden="true" /> : null}
            <span>{label}</span>
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="wd-viz wd-viz-browser">
        <div className="wd-viz-chrome"><i /><i /><i /><b>Layout</b></div>
        <div className="wd-viz-wire">
          <span />
          <span />
          <div><i /><i /><i /></div>
        </div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <ul className="wd-viz wd-viz-layers">
        {["Frontend", "WordPress", "Custom functionality", "Integrations"].map((label, step) => (
          <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span><b /></li>
        ))}
      </ul>
    );
  }
  if (index === 4) {
    return (
      <ul className="wd-viz wd-viz-checks">
        {["Responsive", "Forms", "Performance", "Integrations"].map((label) => (
          <li key={label}><i aria-hidden="true" />{label}</li>
        ))}
      </ul>
    );
  }
  if (index === 5) {
    return (
      <div className="wd-viz wd-viz-launch">
        <div><small>Environment</small><strong>Development</strong></div>
        <span>Deploy</span>
        <div className="is-live"><small>Environment</small><strong>Production</strong></div>
      </div>
    );
  }
  return (
    <div className="wd-viz wd-viz-flow">
      {["Monitor", "Maintain", "Improve"].map((label, step) => (
        <React.Fragment key={label}>
          {step > 0 ? <i aria-hidden="true" /> : null}
          <span>{label}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(3);
  const refs = useRef([]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;

    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };

    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);

  const [title, text] = process[active];

  return (
    <>
      <div className="wd-process" style={{ "--wd-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="wd-process-rail" />
        <div className="wd-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`wd-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="wd-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="wd-step-copy block pb-2">
              <span className="block text-lg font-semibold text-slate-900 font-heading">{stepTitle}</span>
              <span className="mt-1 block text-sm text-slate-600 leading-relaxed">{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="wd-build-stage" aria-live="polite">
        <div className="wd-build-stage-body" key={active}>
          <p className="wd-build-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="wd-build-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`wd-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="w-full flex items-center justify-between gap-6 py-3.5 text-left"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : index)}
            >
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="wd-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`wd-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div>
                <p className="pb-5 max-w-3xl text-slate-600 leading-relaxed">{answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WebDevelopment() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Web Development | BizLogic Developers";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <article data-testid="service-detail-page" className="wd-page bg-white">
      <header className="wd-hero border-b border-slate-200">
        <div className="wd-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="wd-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="wd-hero-item text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 font-heading" style={{ animationDelay: "120ms" }}>
              Web development
            </p>
            <h1 className="wd-hero-item text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Websites Built Around<span className="hidden xl:inline"><br /></span> Your Business.
            </h1>
            <p className="wd-hero-item wd-hero-lead wd-lead max-w-xl text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Custom websites and WordPress solutions designed around your brand, your customers, and the way your business works.
            </p>
            <div className="wd-hero-item wd-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-web-development" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/#portfolio-section" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                View Our Work
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <BrowserMock />
          </div>
        </div>
      </header>

      <section className="wd-section border-b border-slate-200">
        <div className="wd-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <Kicker index="01" label="Service introduction" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">More Than a Website.</h2>
            <p className="wd-lead text-slate-600 leading-relaxed">
              A business website should do more than publish information. It should help people understand what you offer, take action, and connect with the systems that keep your business moving.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <SystemDiagram />
          </div>
        </div>
      </section>

      <section className="wd-section border-b border-slate-200 bg-slate-50/70">
        <div className="wd-wrap">
          <Reveal>
            <Kicker index="02" label="What we build" />
            <h2 className="max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built for the Way Your Business Works.</h2>
          </Reveal>
          <div className="wd-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {builds.map(([number, title, text], index) => (
              <Reveal key={title} delay={(index % 2) * 80} className={`h-full ${index === 0 || index === 5 ? "md:col-span-2" : ""}`}>
                <article className={`wd-card ${index === 0 || index === 5 ? "is-wide" : ""}`}>
                  <span className="wd-card-index">{number}</span>
                  <ArrowRight className="wd-arrow w-4 h-4 text-slate-400" />
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="wd-section border-b border-slate-200">
        <div className="wd-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <Reveal>
            <Kicker index="03" label="WordPress development" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">WordPress Without the Template Feel.</h2>
            <p className="wd-lead text-slate-600 leading-relaxed">
              BizLogic can build and customize WordPress beyond installing a theme. The editing model, the content types, and the connections to other tools are part of the build when the business needs them.
            </p>
            <ul className="wd-wp-caps wd-after">
              {wordpressGroups.map(([type, label, line]) => (
                <li key={label} className="wd-wp-cap">
                  <span className="wd-wp-glyph"><WpGlyph type={type} /></span>
                  <span className="wd-wp-cap-copy">
                    <span className="wd-wp-cap-label">{label}</span>
                    <span className="wd-wp-cap-line">{line}</span>
                  </span>
                  <ArrowRight className="wd-wp-cap-arrow" aria-hidden="true" />
                </li>
              ))}
            </ul>
          </Reveal>
          <WpArchitecture />
        </div>
      </section>

      <section className="wd-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="wd-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <Reveal>
              <Kicker index="04" label="Built to perform" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Fast, Responsive, Ready for Real Users.</h2>
              <p className="wd-lead text-slate-600 leading-relaxed">
                A website has to work for the person on the other side of the screen. Speed, layout, structure, and the basics of security are part of the build.
              </p>
            </Reveal>
            <div className="wd-after">
              <PerformancePanel />
            </div>
          </div>
          <div className="wd-perf-grid">
            {performancePoints.map(([mark, title, text], index) => (
              <Reveal key={title} delay={index * 40} className="h-full">
                <article className="wd-perf-item">
                  <span className="wd-perf-mark"><PerfMark type={mark} /></span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="wd-section border-b border-slate-200">
        <div className="wd-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Kicker index="05" label="Connected systems" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Your Website Should Work With Your Business.</h2>
            <p className="wd-lead text-slate-600 leading-relaxed">
              A website can pass information into the tools a company already uses, when those tools provide a connection. The path depends on the workflow, not on a fixed stack of apps.
            </p>
            <ul className="wd-after flex flex-wrap gap-2">
              {["APIs", "Webhooks", "Payment systems", "Booking platforms", "CRM systems", "Email platforms", "Analytics"].map((item) => (
                <li key={item} className="border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">{item}</li>
              ))}
            </ul>
          </Reveal>
          <div className="lg:col-span-6">
            <ConnectedFlow />
          </div>
        </div>
      </section>

      <section className="wd-section wd-build border-b border-slate-200">
        <div className="wd-wrap">
          <div className="wd-build-intro">
            <Reveal className="max-w-2xl">
              <Kicker index="06" label="How we build" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From First Idea to Launch.</h2>
              <p className="wd-lead text-slate-600 leading-relaxed">
                Scroll the steps, or select one. The active stage is the work in front of you, from the first conversation through support after launch.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="wd-section border-b border-slate-200">
        <div className="wd-wrap">
          <Reveal>
            <Kicker index="07" label="Why BizLogic" />
            <h2 className="max-w-3xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">A Website Should Support the Business Behind It.</h2>
          </Reveal>
          <div className="wd-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {principles.map(([title, text], index) => {
              const wide = index === 0 || index === principles.length - 1;
              return (
              <Reveal key={title} delay={(index % 2) * 80} className={wide ? "md:col-span-2" : ""}>
                <article className={`h-full border border-slate-200 bg-white p-5 sm:p-6 ${wide ? "md:grid md:grid-cols-2 md:gap-8 md:items-center" : ""}`}>
                  <h3 className="text-2xl font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className={`mt-3 text-slate-600 leading-relaxed ${wide ? "md:mt-0" : ""}`}>{text}</p>
                </article>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="wd-section border-b border-slate-200">
        <div className="wd-wrap">
          <Reveal>
            <Kicker index="08" label="FAQ" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions about a website project.</h2>
          </Reveal>
          <div className="wd-after">
            <FaqList />
          </div>
        </div>
      </section>

      <section className="wd-cta">
        <div className="wd-cta-grid" />
        <div className="wd-cta-glow" />
        <div className="wd-wrap relative wd-section">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Build a Website Around Your Business.
          </h2>
          <p className="wd-lead text-slate-300 leading-relaxed">
            Tell us what your business needs from its website, and we&apos;ll help turn the requirements into a digital experience built around how you work.
          </p>
          <div className="wd-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
